import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const Card = ({ c }) => (
  <div className={`w-14 h-20 rounded-lg bg-white flex items-center justify-center text-lg font-bold ${c.s === "♥" || c.s === "♦" ? "text-red-600" : "text-black"}`}>
    {c.r}{c.s}
  </div>
);

export default function BaccaratGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [side, setSide] = useState("player");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const deal = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/tables/baccarat/deal", { bet, side });
      setResult(data); setUser((u) => ({ ...u, coins: data.coins }));
      data.win ? toast.success(`${data.winner} wins · +${data.payout.toLocaleString()}`) : toast(`${data.winner} wins — no win`);
      react({ outcome: data.win ? "win" : "loss", game: "baccarat", big: side === "tie" && data.win });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <GameFrame title="Baccarat" subtitle="Player · Banker · Tie · coins only"
      side={<>
        <div className="glass rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-gray-400 mb-2">Pick</p>
          <div className="grid grid-cols-3 gap-2">
            {[["player", "Player 1:1"], ["banker", "Banker 0.95:1"], ["tie", "Tie 8:1"]].map(([v, l]) => (
              <button key={v} onClick={() => setSide(v)} data-testid={`baccarat-side-${v}`}
                className={`py-2 rounded-lg text-xs font-semibold ${side === v ? "bg-gradient-to-r from-amber-500 to-purple-600" : "bg-white/5 text-gray-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        <BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={busy} onPlay={deal} playLabel={busy ? "Dealing…" : "DEAL"} testid="baccarat-deal" />
      </>}>
      <div className="glass rounded-3xl p-8 border border-amber-500/20 glow-coins">
        <div className="grid grid-cols-2 gap-6">
          {[["player", "Player"], ["banker", "Banker"]].map(([k, label]) => (
            <div key={k} className={`rounded-2xl p-4 border ${result?.winner === k ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10"}`}>
              <p className="text-center text-sm text-gray-400 mb-3">{label} {result ? `· ${k === "player" ? result.player_total : result.banker_total}` : ""}</p>
              <div className="flex gap-2 justify-center min-h-[80px]">
                {(result ? (k === "player" ? result.player : result.banker) : [{ r: "?", s: "" }, { r: "?", s: "" }]).map((c, i) => <Card key={i} c={c} />)}
              </div>
            </div>
          ))}
        </div>
        {result && <p className={`text-center mt-6 px-6 py-3 rounded-xl ${result.win ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-gray-400"}`}>{result.winner.toUpperCase()} wins · {result.win ? `+${result.payout.toLocaleString()} coins` : "You didn't win"}</p>}
      </div>
    </GameFrame>
  );
}
